import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { findIndex, propEq, adjust, isEmpty } from 'ramda'

import usePopupErr from 'app/common/hooks/usePopupErr'
import usePaging, { type Paging, defaultPaging } from 'app/common/hooks/usePaging'
import { api } from 'app/api'
import { COPY_FOOD_ITEMS } from 'app/common/api/requests/food/items/copy'
import type { Dish } from 'app/dataTypes/dishes/dish'
import type { PutValueParams, PutPhotoParams } from 'app/dataTypes/dishes/field'

import {
  GET_FOOD_ITEMS,
  convertParams,
  convertResult,
} from 'app/common/api/requests/food/items/get'

import {
  DELETE_FOOD_ITEMS,
  convertParams as deleteConvertParams,
  convertResult as deleteConvertResult,
} from 'app/common/api/requests/food/items/delete'

import {
  PUT_FOOD_ITEM_FIELD,
  convertParams as putConvertParams,
  convertResult as putConvertResult,
} from 'app/common/api/requests/food/item/field/put'

import { useGeneralSettings } from 'app/dataProviders/generalSettings/hooks'
import { SaleTypeId } from 'app/dataTypes/dishes/saleType'

/**
 *  Работа с блюдами поставщика
 */

export type { Paging }
export type SaveType = PutValueParams['changeItemTypes']
export type Photo = PutPhotoParams['value']

export { defaultPaging }

const emptyFunc = () => {}
const resolveFalse = () => Promise.resolve(false)

const requestDishesError = 'Не удалось получить данные блюд'

const editFieldSuccess = 'Значение поля успешно изменено'
const editFieldError = 'Изменение поля не удалось (api)'
const editFieldErrorApi = 'Изменение поля не удалось'

const deletePhotoSuccess = 'Изображение блюда успешно удалено'
const deletePhotoError = 'Удаление изображения блюда не удалось'
const deletePhotoErrorApi = 'Удаление изображения блюда не удалось (api)'

const updatePhotoSuccess = 'Изображение блюда успешно заменено на новое'
const updatePhotoError = 'Изменение изображения блюда не удалось'
const updatePhotoErrorApi = 'Изменение изображения блюда не удалось (api)'

const deleteDishesError = 'Удаление элементов не удалось'
const deleteDishesSuccess = (itemsLength: number) =>
  itemsLength > 1 ? `Блюда (${itemsLength}) успешно удалены` : 'Блюдо успешно удалено'

const copyDishesError = 'Копирование блюд не удалось'
const copyDishesSuccess = (itemsLength: number) =>
  itemsLength > 1 ? `Блюда (${itemsLength}) успешно скопированы` : 'Блюдо успешно скопировано'

export type State = {
  dishes: Array<Dish>
  nomenclatureActive: boolean
  total: number
  limit: number
  loading: boolean
}

export const defaultState: State = {
  dishes: [],
  nomenclatureActive: false,
  total: 0,
  limit: 25,
  loading: true,
}

export type ApiHandlers = {
  request: () => void | Promise<void>
  fieldUpdate: (params: PutValueParams) => Promise<boolean>
  delete: (dishesIds: Array<string>) => Promise<boolean>
  copy: (dishesIds: Array<number>) => Promise<boolean>
  photoUpdate: (id: string, value: PutPhotoParams['value']) => Promise<boolean>
  photoDelete: (id: string) => Promise<boolean>
}

export const defaultApiHandlers: ApiHandlers = {
  request: emptyFunc,
  fieldUpdate: resolveFalse,
  delete: resolveFalse,
  copy: resolveFalse,
  photoUpdate: resolveFalse,
  photoDelete: resolveFalse,
}

export type Options = {
  limit?: number
  search?: string
  saleTypeId?: SaleTypeId | Array<SaleTypeId>
  onDishes?: (dishes: Array<Dish>) => void
}

const defaultOptions: {
  limit: number
} = {
  limit: 25,
}

export default function useDishes(options: Options = {}): [State, ApiHandlers, Paging] {
  const { limit, search, saleTypeId, onDishes } = { ...defaultOptions, ...options }
  const [popupErr, popup] = usePopupErr()

  const {
    generalSettings: { useNomenclature: nomenclatureActive },
  } = useGeneralSettings()

  const [dishes, setDishes] = useState([] as Array<Dish>)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  const paging = usePaging()
  const { currentPage, setCurrentPage } = paging

  const setSingleDishToDishesState = useCallback(
    (dishItem: Dish) => {
      const index = findIndex(propEq('id', dishItem.id), dishes)
      if (index === -1) {
        return false
      }
      setDishes(adjust(index, (prev) => ({ ...prev, ...dishItem })))
      return true
    },
    [dishes],
  )

  const requestGeneration = useRef(0)

  // запрос данных
  const handleRequestDishes = useCallback(async () => {
    const generation = ++requestGeneration.current
    setLoading(true)
    const res = await api.request(GET_FOOD_ITEMS, {
      cancelable: true,
      error: requestDishesError,
      convertions: {
        convertParams,
        convertResult,
      },
      params: {
        limit,
        search,
        offset: (currentPage - 1) * limit,
        saleTypeId,
      },
    })

    if (generation !== requestGeneration.current) {
      return
    }

    setLoading(false)

    if (res) {
      const { items, total: totalCount } = res
      if (isEmpty(items) && totalCount && currentPage > 1) {
        // нет данных на странице >1, редирект на первую
        setCurrentPage(1)
      }
      setDishes(items)
      setTotal(totalCount)
    }
  }, [limit, search, currentPage, saleTypeId, setCurrentPage])

  // изменение поля в одном элементе
  const handleUpdateField = useCallback(
    async (params: PutValueParams) => {
      const { id } = params
      if (!id) {
        popupErr(editFieldErrorApi)
        return false
      }
      const res = await api.request(PUT_FOOD_ITEM_FIELD, {
        error: editFieldError,
        params,
        convertions: {
          convertParams: putConvertParams,
          convertResult: putConvertResult,
        },
      })

      if (res && setSingleDishToDishesState(res)) {
        popup(editFieldSuccess)
        return true
      }
      popupErr(editFieldErrorApi)
      return false
    },
    [popup, popupErr, setSingleDishToDishesState],
  )

  // удаление фотографии
  const handlePhotoDelete = useCallback(
    async (id: string) => {
      const res = await api.request(PUT_FOOD_ITEM_FIELD, {
        error: deletePhotoError,
        params: {
          valueType: 'photo',
          id,
          field: 'photo',
          value: null,
        },
        convertions: {
          convertParams: putConvertParams,
          convertResult: putConvertResult,
        },
      })

      if (res && setSingleDishToDishesState(res)) {
        popup(deletePhotoSuccess)
        return true
      }
      popupErr(deletePhotoErrorApi)
      return false
    },
    [popup, popupErr, setSingleDishToDishesState],
  )

  // обновление фотографии
  const handlePhotoUpdate = useCallback(
    async (id: string, value: PutPhotoParams['value']) => {
      if (!id) {
        popupErr(updatePhotoErrorApi)
        return false
      }
      const res = await api.request(PUT_FOOD_ITEM_FIELD, {
        error: updatePhotoError,
        params: {
          valueType: 'photo',
          id,
          field: 'photo',
          value,
        },
        convertions: {
          convertParams: putConvertParams,
          convertResult: putConvertResult,
        },
      })

      if (res && setSingleDishToDishesState(res)) {
        popup(updatePhotoSuccess)
        return true
      }
      popupErr(updatePhotoErrorApi)
      return false
    },
    [popup, popupErr, setSingleDishToDishesState],
  )

  // удаление блюд
  const handleDeleteDishes = useCallback(
    async (itemIds: Array<string>) => {
      const res = await api.request(DELETE_FOOD_ITEMS, {
        error: deleteDishesError,
        convertions: {
          convertParams: deleteConvertParams,
          convertResult: deleteConvertResult,
        },
        params: {
          itemIds,
        },
      })

      if (res) {
        popup(deleteDishesSuccess(itemIds.length))
        handleRequestDishes()
        return true
      }
      return false
    },
    [handleRequestDishes, popup],
  )

  // копирование блюд
  const handleCopyDishes = useCallback(
    async (itemIds: Array<number>) => {
      const res = await api.request(COPY_FOOD_ITEMS, {
        error: copyDishesError,
        params: {
          items: itemIds,
        },
      })

      if (res) {
        popup(copyDishesSuccess(itemIds.length))
        handleRequestDishes()
        return true
      }
      return false
    },
    [handleRequestDishes, popup],
  )

  // сброс номера страницы
  useEffect(() => {
    if (currentPage > 1) {
      setCurrentPage(1)
    }
  }, [search, saleTypeId]) // eslint-disable-line react-hooks/exhaustive-deps

  // запрос блюд
  useEffect(() => {
    handleRequestDishes()
  }, [handleRequestDishes])

  // обновление состояния блюд
  useEffect(() => {
    if (onDishes) {
      onDishes(dishes)
    }
  }, [dishes]) // eslint-disable-line react-hooks/exhaustive-deps

  const state = useMemo(
    () => ({
      dishes,
      nomenclatureActive,
      total,
      limit,
      loading,
    }),
    [dishes, total, limit, loading, nomenclatureActive],
  )

  const apiHandlers = useMemo(
    () => ({
      request: handleRequestDishes,
      fieldUpdate: handleUpdateField,
      delete: handleDeleteDishes,
      copy: handleCopyDishes,
      photoDelete: handlePhotoDelete,
      photoUpdate: handlePhotoUpdate,
    }),
    [
      handleRequestDishes,
      handleUpdateField,
      handleDeleteDishes,
      handleCopyDishes,
      handlePhotoDelete,
      handlePhotoUpdate,
    ],
  )

  return [state, apiHandlers, paging]
}
